import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, PlusIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import AuthenticatedImage from './AuthenticatedImage';
import { dressService, Dress } from '../services/dress';
import { designerService, Designer } from '../services/designer';

interface DressPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDresses: (selections: Array<{ dressId: string; note?: string }>) => Promise<void>;
  existingDressIds: string[];
}

const DressPickerModal: React.FC<DressPickerModalProps> = ({
  isOpen,
  onClose,
  onSelectDresses,
  existingDressIds
}) => {
  const [dresses, setDresses] = useState<Dress[]>([]);
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDesignerId, setSelectedDesignerId] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDresses, setSelectedDresses] = useState<Map<string, Dress>>(new Map());
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);

  // Fetch designers on mount
  useEffect(() => {
    const fetchDesigners = async () => {
      try {
        const response = await designerService.getDesigners({ showDisabled: false });
        setDesigners(response.data);
      } catch (error) {
        console.error('Failed to fetch designers:', error);
      }
    };
    fetchDesigners();
  }, []);

  const fetchDresses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dressService.getDresses({
        page,
        limit: 12,
        search: searchTerm || undefined,
        designerId: selectedDesignerId || undefined
      });
      setDresses(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Failed to fetch dresses:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, selectedDesignerId]);

  useEffect(() => {
    if (isOpen) {
      fetchDresses();
    }
  }, [isOpen, fetchDresses]);

  useEffect(() => {
    // Reset page when filters change
    setPage(1);
  }, [searchTerm, selectedDesignerId]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchDresses();
  };

  const handleToggleDress = (dress: Dress) => {
    if (existingDressIds.includes(dress.id)) return;

    setSelectedDresses(prev => {
      const newMap = new Map(prev);
      if (newMap.has(dress.id)) {
        newMap.delete(dress.id);
      } else {
        newMap.set(dress.id, dress);
      }
      return newMap;
    });
  };

  const handleRemoveSelection = (dressId: string) => {
    setSelectedDresses(prev => {
      const newMap = new Map(prev);
      newMap.delete(dressId);
      return newMap;
    });
  };

  const handleAddDresses = async () => {
    if (selectedDresses.size === 0) return;

    setAdding(true);
    try {
      const selections = Array.from(selectedDresses.keys()).map(dressId => ({
        dressId,
        note: note || undefined
      }));
      await onSelectDresses(selections);
      setSelectedDresses(new Map());
      setNote('');
      onClose();
    } catch (error) {
      console.error('Failed to add dresses:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    setSelectedDresses(new Map());
    setNote('');
    setSearchTerm('');
    setSelectedDesignerId('');
    setPage(1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Dresses to Staff Picks" size="xl">
      <div className="space-y-4">
        {/* Search and Filter */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search dresses..."
              className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <select
            value={selectedDesignerId}
            onChange={(e) => setSelectedDesignerId(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Designers</option>
            {designers.map((designer) => (
              <option key={designer.id} value={designer.id}>
                {designer.name}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
          >
            Search
          </button>
        </form>

        {/* Selected Count Badge */}
        {selectedDresses.size > 0 && (
          <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 px-3 py-2 rounded-md">
            <CheckIcon className="h-4 w-4" />
            <span>{selectedDresses.size} dress{selectedDresses.size !== 1 ? 'es' : ''} selected</span>
          </div>
        )}

        {/* Dress Grid */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {dresses.map((dress) => {
              const isExisting = existingDressIds.includes(dress.id);
              const isSelected = selectedDresses.has(dress.id);

              return (
                <button
                  key={dress.id}
                  onClick={() => handleToggleDress(dress)}
                  disabled={isExisting}
                  className={`relative rounded-lg border p-2 text-left transition-all ${
                    isExisting
                      ? 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-60'
                      : isSelected
                      ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                  }`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-[3/4] w-full overflow-hidden rounded bg-gray-100">
                    {dress.thumbnailUrl ? (
                      <AuthenticatedImage
                        src={dress.thumbnailUrl}
                        alt={dress.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-gray-400">
                        No image
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{dress.name}</p>
                    {dress.designer && (
                      <p className="text-xs text-gray-500 truncate">{dress.designer.name}</p>
                    )}
                  </div>

                  {/* Status Icon */}
                  {isExisting && (
                    <div className="absolute top-2 right-2 rounded-full bg-gray-500 p-1">
                      <CheckIcon className="h-4 w-4 text-white" />
                    </div>
                  )}
                  {isSelected && !isExisting && (
                    <div className="absolute top-2 right-2 rounded-full bg-primary-500 p-1">
                      <CheckIcon className="h-4 w-4 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t pt-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Selected Dresses Preview */}
        {selectedDresses.size > 0 && (
          <div className="border-t pt-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">Selected Dresses:</p>
            <div className="flex flex-wrap gap-2">
              {Array.from(selectedDresses.entries()).map(([dressId, dress]) => (
                <div
                  key={dressId}
                  className="flex items-center gap-2 bg-primary-50 px-3 py-1.5 rounded-full text-sm"
                >
                  <span className="text-primary-700">{dress.name}</span>
                  <button
                    onClick={() => handleRemoveSelection(dressId)}
                    className="text-primary-500 hover:text-primary-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note for all selected dresses (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g., Perfect for garden wedding, matches her preference for lace..."
                rows={2}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            onClick={handleClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAddDresses}
            disabled={selectedDresses.size === 0 || adding}
            className="flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <PlusIcon className="h-5 w-5" />
            {adding ? 'Adding...' : `Add ${selectedDresses.size > 0 ? selectedDresses.size : ''} to Staff Picks`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DressPickerModal;
